import React, { useMemo } from 'react';
import { Users } from 'lucide-react';
import type { StudioSpecialist } from '../../services/studioService';
import AgentSeat from './AgentSeat';

interface Props {
  specialists: StudioSpecialist[];
  /** The catalog profile ids already on the crew — who holds each craft seat, if anyone. */
  memberProfileIds: string[];
  /** Hire this agent onto the production's crew — owned by the Studio. Rejects with a human message. */
  onRecruit: (agentId: string, replacesAgentId?: string) => Promise<void>;
  /** Take the holder of a craft off the production; the crew chat follows (owner 2026-09-13). */
  onDismiss?: (agentId: string) => Promise<void>;
  /** Called after an agent is added, so the parent can refresh the chat roster. */
  onAdded?: () => void;
}

/** "Camera & motion" — a craft reads as a seat title, not as a lowercase tag. */
const titleCase = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * CrewStrip — the craft seats beside the director's chair. The platform principle: a workflow is staffed by the
 * RIGHT specialists, each owning one craft (camera & motion, look & art direction, photoreal frames).
 *
 * EVERY CRAFT IS A SEAT (owner law 2026-08-16: one component for every agent pick). This used to be a row of
 * name chips with a Plus and an Add all — a one-tap add that never let you meet anyone. Now each craft states
 * who holds it and opens the shared hiring room (portrait at size, their gallery, the full bio, one deliberate
 * Recruit). Filled seats stay visible: the crew page exists to show WHO is on the production.
 */
const CrewStrip: React.FC<Props> = ({ specialists, memberProfileIds, onRecruit, onDismiss, onAdded }) => {
  const present = useMemo(() => new Set(memberProfileIds), [memberProfileIds]);

  if (!specialists.length) return null;

  const hire = async (agentId: string) => {
    await onRecruit(agentId);   // a refusal propagates to the seat, which says why
    onAdded?.();
  };

  return (
    <div className="mb-4 rounded-xl border border-secondary-200 p-3.5 dark:border-secondary-800">
      <div className="mb-1 flex items-center gap-2">
        <Users className="h-4 w-4 text-secondary-700 dark:text-secondary-300" />
        <span className="text-xs font-semibold text-secondary-900 dark:text-white">Your creative crew</span>
      </div>
      <p className="mb-3 text-[11px] text-secondary-400 dark:text-secondary-500">
        One specialist per craft — the agent holding a seat brings their knowledge to every shot.
      </p>

      <div className="space-y-3">
        {specialists.map((s) => (
          <AgentSeat
            key={s.id}
            testId={`crew-seat-${s.id}`}
            label={titleCase(s.strength)}
            seatTitle={`Choose your ${s.strength} lead`}
            hiredLabel="On your crew"
            verb="Recruit"
            candidates={[{ agentId: s.id, name: s.name, note: titleCase(s.strength) }]}
            currentId={present.has(s.id) ? s.id : null}
            onRecruit={hire}
            onDismiss={onDismiss}
          />
        ))}
      </div>
    </div>
  );
};

export default CrewStrip;
